import { QueryResult } from "@apollo/client";
import { Typography } from "@mui/material";
import { GetEventsByNameQuery, GetEventsByNameQueryVariables, GetOrganisationsByNameQuery, GetOrganisationsByNameQueryVariables } from "components/layout/home/SearchEventAndOrganisation.gql.types";
import { SearchEventAndOrganisationResultsSkeleton } from "components/layout/home/SearchEventAndOrganisationResultsSkeleton";
import { SearchEventCard } from "components/layout/home/SearchEventCard";
import { SearchOrganisationCard } from "components/layout/home/SearchOrganisationCard";
import { SimpleList } from "components/layout/lists/SimpleList";
import { useFeatureFlags } from "components/providers/featureFlags/useFeatureFlags";
import { T } from "components/util/t";
import { Link } from "react-router-dom";
import { trackClick, trackingCategory } from "utils/tracking";
import { SearchEventAndOrganisationContainer } from "./SearchEventAndOrganisationContainer";

export type SearchEventAndOrganisationResult = {
    type: "event",
    data: NonNullable<
        NonNullable<
            QueryResult<GetEventsByNameQuery, GetEventsByNameQueryVariables>["data"]
        >["eventsByName"]
    >[number]
} | {
    type: "organisation",
    data: NonNullable<
        NonNullable<
            QueryResult<GetOrganisationsByNameQuery, GetOrganisationsByNameQueryVariables>["data"]
        >["organisationsByName"]
    >[number]
}

export const SearchEventAndOrganisationResults: React.FC<{
    eventSearch?: string;
    orgSearch?: string;
    eventLiveSearch?: string;
    loading: boolean;
    oldItems: { title: string; description: string; to: string }[];
    searchResults: SearchEventAndOrganisationResult[];
}> = ({ loading, oldItems: items, eventSearch, orgSearch, eventLiveSearch, searchResults }) => {
    const enableFeatureFlags = useFeatureFlags();

    if (!eventSearch?.length && !orgSearch?.length && !eventLiveSearch?.length) {
        return null;
    }

    if (loading) {
        return <SearchEventAndOrganisationResultsSkeleton />;
    }

    if (!enableFeatureFlags) {
        if (!items.length) {
            return <T search={orgSearch}>no_search_results_found</T>;
        }

        return (
            <>
                <SimpleList>{items}</SimpleList>
                <Typography mt={3} mb={6}>
                    <T>browse_all_organisations</T>
                </Typography>
            </>
        );
    }

    if (!searchResults.length) {
        return <T search={orgSearch}>no_search_results_found</T>;
    }

    return (
        <>
            <SearchEventAndOrganisationContainer>
                {searchResults.map((result) =>
                    result.type === "event" ? (
                        <Link
                            to={`/events/${result.data.id}`} key={`event-${result.data.id}`}
                            onClick={trackClick("Event card", trackingCategory.searchEventAndOrganisation)}
                        >
                            <SearchEventCard key={result.data.id} event={result.data} />
                        </Link>
                    ) : (
                        <Link
                            to={`/${result.data.shortName}`} key={`organisation-${result.data.id}`}
                            onClick={trackClick("Organisation card", trackingCategory.searchEventAndOrganisation)}
                        >
                            <SearchOrganisationCard key={result.data.id} organisation={result.data} />
                        </Link>
                    )
                )}
            </SearchEventAndOrganisationContainer>
            <Typography mt={3} mb={6} textAlign="center">
                <T>browse_all_organisations</T>
            </Typography>
        </>
    );
};
